<script lang="ts" setup>
const { getContinentAttributes, getCountryAttributes, getCountyAttributes, getMetaAttributes, getStateAttributes } = useApiAttributes()
const { t } = useI18n()
const api = useApi()

const { data: response, execute, status } = useAsyncData(
  'doc:locations-search-cities',
  () => api.locations.cities.fetch(),
  { immediate: false },
)

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/locations/cities',
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.page'),
    name: 'page',
    optional: true,
    type: 'number',
  },
  {
    description: t('attributes.search'),
    name: 'search',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.size'),
    name: 'size',
    optional: true,
    type: 'number',
  },
]

const responseAttributes: ApiAttribute[] = [
  {
    description: t('response.cities'),
    name: 'cities',
    type: 'array',

    attributes: [
      {
        description: t('response.code'),
        name: 'code',
        parent: 'city',
        type: 'string',
      },
      {
        attributes: getContinentAttributes({ parent: 'city' }),
        description: t('response.continent'),
        name: 'continent',
        parent: 'city',
        type: 'object',
      },
      {
        attributes: getCountryAttributes({ parent: 'city' }),
        description: t('response.country'),
        name: 'country',
        parent: 'city',
        type: 'object',
      },
      {
        attributes: getCountyAttributes({ parent: 'city' }),
        description: t('response.county'),
        name: 'county',
        parent: 'city',
        type: 'object',
      },
      {
        description: t('response.latitude'),
        name: 'latitude',
        parent: 'city',
        type: 'string',
      },
      {
        description: t('response.longitude'),
        name: 'longitude',
        parent: 'city',
        type: 'string',
      },
      {
        description: t('response.name'),
        name: 'name',
        parent: 'city',
        type: 'string',
      },
      {
        description: t('response.postcode'),
        name: 'postcode',
        parent: 'city',
        type: 'string',
      },
      {
        attributes: getStateAttributes({ parent: 'city' }),
        description: t('response.state'),
        name: 'state',
        parent: 'city',
        type: 'string',
      },
    ],
  },
  getMetaAttributes(),
]

async function onVisibilityChange(visibility: boolean) {
  if (visibility && !response.value) {
    await execute()
  }
}
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "page": "The page to fetch (default to 1).",
      "search": "An additional search query applied to the city name.",
      "size": "The number of cities to be returned (between 1 and 20). Default to 10."
    },
    "response": {
      "cities": "The cities that match your conditions.",
      "code": "The city code.",
      "continent": "The city's continent.",
      "country": "The city's country.",
      "county": "The city's county.",
      "latitude": "The city's latitude.",
      "longitude": "The city's longitude.",
      "name": "The city's name.",
      "postcode": "The city's postcode.",
      "state": "The city's state."
    }
  },
  "es": {
    "attributes": {
      "page": "La página a obtener (por defecto 1).",
      "search": "Una consulta de búsqueda adicional aplicada al nombre de la ciudad.",
      "size": "El número de ciudades a devolver (entre 1 y 20). Por defecto 10."
    },
    "response": {
      "cities": "Las ciudades que coinciden con tus condiciones.",
      "code": "El código de la ciudad.",
      "continent": "El continente de la ciudad.",
      "country": "El país de la ciudad.",
      "county": "El condado de la ciudad.",
      "latitude": "La latitud de la ciudad.",
      "longitude": "La longitud de la ciudad.",
      "name": "El nombre de la ciudad.",
      "postcode": "El código postal de la ciudad.",
      "state": "El estado de la ciudad."
    }
  },
  "fr": {
    "attributes": {
      "page": "La page à récupérer (par défaut 1).",
      "search": "Une recherche additionnelle appliquée au nom de la ville.",
      "size": "Le nombre de villes à retourner (entre 1 et 20). Par défaut 10."
    },
    "response": {
      "cities": "Les villes qui correspondent à vos conditions.",
      "code": "Le code de la ville.",
      "continent": "Le continent de la ville.",
      "country": "Le pays de la ville.",
      "county": "Le département de la ville.",
      "latitude": "La latitude de la ville.",
      "longitude": "La longitude de la ville.",
      "name": "Le nom de la ville.",
      "postcode": "Le code postal de la ville.",
      "state": "La région de la ville."
    }
  }
}
</i18n>
